import React, {Fragment} from 'react';
import {flatten} from 'lodash';

import {emptyArray} from '@packages/utilities/fp/utils';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {percent, px} from '@packages/systems/core/utils/WebflowCssSubset';
import {
  WF_COLLECTION_DATA_KEY,
  WF_TEMPLATE_ID_DATA_KEY,
  WF_TEMPLATE_TYPE,
} from '@packages/systems/commerce/constants';
import {getCommerceDataProperty} from './ContextUtils';

import type {
  AtomRenderProps,
  ChildRender,
  ContextType,
} from '@packages/systems/wfdl';
import {
  iconName,
  qualify,
  pluginName,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {withSystem, Plugins, Atom} from '@packages/systems/wfdl';
import {excludeSearchByDefaultType} from '@packages/systems/search';
import {
  TListOfElement,
  TTypeConstructor,
  TRecord,
} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment, useTemplate} from '@packages/systems/render';
import {
  getChildren,
  getInPojo,
  getTagOrElse,
} from '@packages/utilities/data-utils';
import {UserItem} from './utils/OrderItemsUtils';

const {SSR, EXPORT} = WebflowEnvironments;

const getUserItems = getCommerceDataProperty('userItems', []);

const renderUserItem =
  ({
    context,
    renderChild,
    children,
  }: {
    context: ContextType;
    renderChild: ChildRender;
    children: unknown[];
  }) =>
  (item: any, itemIndex: number) =>
    children.map((child) =>
      renderChild(
        child,
        // TODO (ecommerce): Memoize commerce context mutations
        context.set('data', item),
        // hack; when using mockOrder, we don't get ids
        item.id || `orderItem-${itemIndex}`,
        itemIndex
      )
    );

const createTemplateId = (id: string) => `wf-template-${id}`;

const renderUserItems = (
  {renderChild, context, data}: AtomRenderProps,
  userItems: Array<UserItem>
) => {
  // get previewItems count passed down using context from commerceCartWrapper
  // This previewItems count limits the number of items in the list for cart preview
  const previewItems: number = context.get('previewItems') || 0;

  const renderUserItemWithContext = renderUserItem({
    context,
    renderChild,
    children: getChildren(data),
  });

  return userItems.length > 0
    ? flatten(userItems.slice(0, previewItems).map(renderUserItemWithContext))
    : null;
};

const CommerceCartList = Atom.createRender((prevProps) => {
  const {
    context: prevContext,
    attributes,
    classes,
    data,
    generateAttributes,
    elementId,
  } = prevProps;
  const userItems = getUserItems(prevContext);
  const sampleItems = prevContext.get('sampleItems', []);

  // TODO (ecommerce): Memoize commerce context mutations
  const context = prevContext.update(
    'bindingPathPrefix',
    emptyArray,
    (bindingPathPrefix) => bindingPathPrefix.concat('userItems[]')
  );

  const props = {...prevProps, context} as const;

  const runtimeEnvironment = useRuntimeEnvironment();
  const template = useTemplate();

  const Tag = getTagOrElse(getInPojo(data, ['tag']), 'div');
  if (runtimeEnvironment === SSR || runtimeEnvironment === EXPORT) {
    const content = renderUserItems(props, sampleItems);

    const templateContent = template(content);
    const tagAttributes = {
      ...generateAttributes(attributes, classes),
      [WF_COLLECTION_DATA_KEY]: 'database.commerceOrder.userItems',
      [WF_TEMPLATE_ID_DATA_KEY]: createTemplateId(elementId),
    } as const;
    return (
      <Fragment>
        <script type={WF_TEMPLATE_TYPE} id={createTemplateId(elementId)}>
          {templateContent}
        </script>
        <Tag {...tagAttributes}>{content}</Tag>
      </Fragment>
    );
  } else {
    return (
      <Tag {...generateAttributes(attributes, classes)}>
        {renderUserItems(props, userItems)}
      </Tag>
    );
  }
});

const config = createIntegratedAtom({
  displayName: 'Cart List',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Dynamo'), iconName('CollectionList')),
  },
  showEmpty: true,
  constraints: {
    wraps: ['CommerceCartItem'],
    ancestors: [{is: 'CommerceCartForm', rule: 'ExactlyOne'}],
  },
  style: {
    base: {
      main: {
        noPseudo: {
          overflow: 'auto',
          paddingTop: px(12),
          paddingBottom: px(12),
          paddingLeft: px(24),
          paddingRight: px(24),
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: percent(0),
          WebkitOverflowScrolling: 'touch',
        },
      },
    },
  },
  render: CommerceCartList,
  fields: {
    search: withSystem(
      {private: true},
      TRecord({
        exclude: excludeSearchByDefaultType,
      })
    ),
    tag: withSystem(
      {
        label: 'Tag',
        private: true,
        defaultValue: 'div',
      },
      TTypeConstructor(qualify(pluginName('Basic'), typeName('TagsDropdown')))
    ),
    children: TListOfElement,
  },
});

export default config;
